import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

/**
 * Sourcing lists — ALL | SAVED | SHORTLISTED.
 *
 * One thunk routed by `filterby`, the same shape as getAllCandidatesForInterviewThunk:
 * the caller passes the sub-tab, the thunk echoes it back in the payload, and the slice
 * files the rows into that sub-tab's list.
 */
export const getSourcingCandidatesThunk = createAsyncThunk(
  'findCandidates/getSourcingCandidates',
  async (
    {
      filterby = 'ALL',
      job_id = null,
      search_id = null,
      search = '',
      filters = {},
      page = 1,
      limit = 10,
      orgId,
      recruiterId,
    },
    { rejectWithValue, signal },
  ) => {
    try {
      // Always set in practice — the screen holds a job in scope. Guarded anyway so a
      // caller without one degrades to the org-wide read rather than sending job_id=null.
      // search_id narrows that further to one search's candidates.
      const scope = { ...(job_id ? { job_id } : {}), ...(search_id ? { search_id } : {}) };
      const q = search ? { search } : {};
      // Empty ends are dropped rather than sent as null, so a one-sided window
      // ("sourced since the 1st") works without a closing bound.
      const [sourcedFrom, sourcedTo] = filters.sourcedRange ?? [];
      const ranges = {
        ...(sourcedFrom ? { sourced_from: sourcedFrom } : {}),
        ...(sourcedTo ? { sourced_to: sourcedTo } : {}),
        ...(filters.status ? { status: filters.status } : {}),
      };

      let url;
      let params;
      if (filterby === 'SAVED') {
        url = `${SOURCING_URL}/sourcing/saved`;
        params = { recruiter_id: recruiterId, organisation_id: orgId, ...scope, ...q, ...ranges, page, limit };
      } else if (filterby === 'SHORTLISTED') {
        url = `${SOURCING_URL}/sourcing/shortlist`;
        params = { organisation_id: orgId, ...scope, ...q, ...ranges, page, limit };
      } else {
        url = `${SOURCING_URL}/sourcing/sourced`;
        params = { org_id: orgId, recruiter_id: recruiterId, ...scope, ...q, ...ranges, page, limit };
      }

      const response = await axios.get(url, { params, signal });
      const candidates = response.data.candidates || [];

      if (!Array.isArray(candidates)) {
        throw new Error('Invalid data format received from server');
      }

      return {
        candidates,
        total_count: response.data.total ?? candidates.length,
        filterby,
      };
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
        throw error; // let Redux Toolkit handle the abort natively
      }
      console.error('getSourcingCandidatesThunk ERROR:', error);
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to load candidates. Please try again.',
      );
    }
  },
);

/**
 * Outreach lists — ALL | CONTACTED | REPLIED, with Replied further refined by the
 * "Status :" select (All | Qualified | Not Qualified).
 *
 * All of them hit /sourcing/contacted and differ only by `stage`, so the row shape is
 * identical across them.
 */
const OUTREACH_STAGE = {
  ALL: null,
  CONTACTED: 'awaiting',
  REPLIED: 'replied',
};

// Qualified/Not Qualified are a narrower `replied` — a submission exists either way,
// it's the verdict on it that differs.
const REPLIED_STATUS_STAGE = {
  All: 'replied',
  Qualified: 'qualified',
  'Not Qualified': 'not_qualified',
};

export const getOutreachCandidatesThunk = createAsyncThunk(
  'findCandidates/getOutreachCandidates',
  async (
    {
      filterby = 'ALL',
      subFilter = 'All',
      job_id = null,
      search_id = null,
      search = '',
      page = 1,
      limit = 10,
      orgId,
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const stage =
        filterby === 'REPLIED'
          ? (REPLIED_STATUS_STAGE[subFilter] ?? 'replied')
          : (OUTREACH_STAGE[filterby] ?? null);
      const params = {
        org_id: orgId,
        ...(job_id ? { job_id } : {}),
        ...(search_id ? { search_id } : {}),
        ...(stage ? { stage } : {}),
        ...(search ? { search } : {}),
        page,
        limit,
      };

      const response = await axios.get(`${SOURCING_URL}/sourcing/contacted`, { params, signal });
      const candidates = response.data.candidates || [];

      if (!Array.isArray(candidates)) {
        throw new Error('Invalid data format received from server');
      }

      return {
        candidates,
        total_count: response.data.total ?? candidates.length,
        filterby,
      };
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
        throw error;
      }
      console.error('getOutreachCandidatesThunk ERROR:', error);
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to load candidates. Please try again.',
      );
    }
  },
);

/**
 * Qualified outreach candidates, for the ATS's "Outreached Candidates" sub-tab on
 * Screened Candidates.
 *
 * Same `stage=qualified` read as the Replied → Qualified refinement above, but that
 * screen pages server-side while Screened Candidates paginates client-side over a
 * full array. So this walks every page and hands back the whole qualified set,
 * capped so a large org can't spin here indefinitely.
 *
 * `jobId` null means org-wide — the "All Jobs" scope.
 *
 * Callers may also pass `jobRole`. It plays no part in the request — candidateManagementSlice
 * reads it off `meta.arg` to stamp the scope's job onto rows that arrive without one.
 */
const OUTREACH_PAGE_SIZE = 100;
const OUTREACH_MAX_PAGES = 20;

export const getQualifiedOutreachCandidatesThunk = createAsyncThunk(
  'findCandidates/getQualifiedOutreachCandidates',
  async ({ orgId, jobId = null }, { rejectWithValue, signal }) => {
    try {
      if (!orgId) return [];

      const collected = [];
      let page = 1;
      let total = 0;

      // eslint-disable-next-line no-constant-condition
      while (page <= OUTREACH_MAX_PAGES) {
        const response = await axios.get(`${SOURCING_URL}/sourcing/contacted`, {
          params: {
            org_id: orgId,
            stage: 'qualified',
            ...(jobId ? { job_id: jobId } : {}),
            page,
            limit: OUTREACH_PAGE_SIZE,
          },
          signal,
        });

        const batch = response.data.candidates || [];
        if (!Array.isArray(batch)) {
          throw new Error('Invalid data format received from server');
        }
        collected.push(...batch);
        total = response.data.total ?? collected.length;

        // A short page is the last page, whatever `total` claims.
        if (batch.length < OUTREACH_PAGE_SIZE || collected.length >= total) break;
        page += 1;
      }

      return collected;
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
        throw error;
      }
      console.error('getQualifiedOutreachCandidatesThunk ERROR:', error);
      return rejectWithValue(
        error.response?.data?.detail ||
          'Failed to load outreached candidates. Please try again.',
      );
    }
  },
);

/**
 * The searches run under one job role — the options behind the "Search :" filter.
 *
 * `all=true` because this is a picker, not a recent-activity list: a search from last week
 * is still a scope you'd want to return to, and the endpoint's default window is 24h.
 * Rows carry their own shortlisted/saved/contacted counts, which the dropdown doesn't use
 * but the Sourcing screen's recent-searches list does — same endpoint, one shape.
 */
export const getJobSearchesThunk = createAsyncThunk(
  'findCandidates/getJobSearches',
  async ({ jobId }, { rejectWithValue, signal }) => {
    try {
      if (!jobId) return [];
      const response = await axios.get(`${SOURCING_URL}/sourcing/searches`, {
        params: { job_id: jobId, all: true, limit: 50 },
        signal,
      });
      return response.data.searches || [];
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
        throw error;
      }
      console.error('getJobSearchesThunk ERROR:', error);
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to load searches for this job role.',
      );
    }
  },
);

/**
 * Every job role the recruiter can source for — the same /sourcing/jobs list the search
 * screen's picker uses, not just the roles already searched under. Find Candidates is
 * entered by choosing a role, so the picker screen and the in-page dropdown have to offer
 * the same set: a role picked before any search has run must still name itself in the header.
 *
 * Normalised to `job_role` so callers don't have to know this endpoint spells it `jobrole`.
 *
 * The per-role counts come from /sourcing/job-roles, which knows only the roles already
 * searched under. Merging the two beats switching endpoints: a role with no sourcing yet
 * still lists, it just reads zero. Counts are decoration on the picker card, so a failure
 * there degrades to zeros rather than costing the recruiter the whole list.
 */
/**
 * How long cached job roles + counts stay usable before a `force` refresh is honoured.
 * 60s matches the server's own org-identity cache window and keeps a recruiter's freshly
 * shortlisted candidate visible in the picker counts within a minute.
 */
const JOB_ROLES_TTL_MS = 60_000;

export const getSourcingJobsThunk = createAsyncThunk(
  'findCandidates/getSourcingJobs',
  async (_arg, thunkAPI) => {
    try {
      const createdBy = localStorage.getItem('org_admin_id');

      const [response, rolesResponse] = await Promise.all([
        axios.get(`${SOURCING_URL}/sourcing/jobs`, { params: { created_by: createdBy } }),
        axios.get(`${SOURCING_URL}/sourcing/job-roles`).catch((error) => {
          console.error('getSourcingJobsThunk counts ERROR:', error);
          return { data: {} };
        }),
      ]);

      const jobs = response.data.jobs;
      if (!jobs || !Array.isArray(jobs)) {
        throw new Error('Invalid data format received from server');
      }

      const countsByJobId = new Map(
        (rolesResponse.data.job_roles || []).map((role) => [role.job_id, role]),
      );

      return jobs.map((job) => {
        const counts = countsByJobId.get(job.job_id);
        return {
          job_id: job.job_id,
          job_role: job.jobrole || 'Untitled role',
          posting_date: job.posting_date ?? null,
          // Null for a role nothing has been sourced under yet — the picker sorts those last.
          last_sourced_at: counts?.last_sourced_at ?? null,
          sourced_count: counts?.sourced_count ?? 0,
          shortlist_count: counts?.shortlist_count ?? 0,
          saved_count: counts?.saved_count ?? 0,
          contacted_count: counts?.contacted_count ?? 0,
        };
      });
    } catch (error) {
      console.error('getSourcingJobsThunk ERROR:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  },
  {
    /**
     * Skip the fetch when the store already has a good-enough answer.
     *
     * This thunk costs two API calls and /sourcing/job-roles is the slowest endpoint in the
     * app, so it used to fire on every mount of both the picker and the Find Candidates
     * header — walking picker -> Find Candidates paid for the same two calls twice (and
     * twice more again under StrictMode's double-invoked effects in dev).
     *
     * `jobRoles` is already treated as cached state: clearFindCandidatesSlice deliberately
     * preserves it across tab resets so the dropdown doesn't flash empty. This makes the
     * fetching side agree with that.
     *
     * Two levels:
     *   - no data            -> always fetch.
     *   - `{ force: true }`  -> the caller needs current counts (the picker cards show
     *                           shortlisted/contacted/saved). Honoured, but only once the
     *                           cached copy is older than JOB_ROLES_TTL_MS, so bouncing
     *                           between the picker and the table doesn't refetch each time.
     *   - otherwise          -> reuse whatever is in the store.
     *
     * The TTL is an age bound, not invalidation-on-change, and that is deliberate: the
     * counts are ORG-wide, so a teammate shortlisting someone changes them without this
     * client ever knowing. Nothing local can detect that; capping staleness is the honest
     * guarantee. Anything needing exactness must read the server, not this cache.
     */
    condition: (arg, { getState }) => {
      const state = getState().findCandidatesSlice;
      if (state.jobsLoading) return false;                 // one already in flight
      if (!(state.jobRoles && state.jobRoles.length)) return true;   // nothing cached
      if (!arg?.force) return false;                       // cached copy is fine
      const age = Date.now() - (state.jobRolesFetchedAt ?? 0);
      return age > JOB_ROLES_TTL_MS;                       // refresh only if stale
    },
  },
);
